import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import apiClient from '@/lib/api';
import { Users, Package, Activity, ShieldCheck, TrendingUp, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get('/admin/stats')
      .then(res => setStats(res.data))
      .catch(() => toast.error('Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="w-10 h-10 border-4 border-pista border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const cards = [
    {
      title: 'Total Users',
      value: stats.users.total,
      subtitle: `+${stats.users.new_24h} last 24h`,
      icon: Users,
      testId: 'stat-users'
    },
    {
      title: 'Active Sessions',
      value: stats.sessions.total_active,
      subtitle: `${stats.activity_24h.successful_logins} logins today`,
      icon: Activity,
      testId: 'stat-sessions'
    },
    {
      title: 'OAuth Apps',
      value: stats.applications.total_developer_apps,
      subtitle: `${stats.applications.total_authorizations} authorizations`,
      icon: Package,
      testId: 'stat-apps'
    },
    {
      title: '2FA Adoption',
      value: `${stats.users['2fa_adoption_rate']}%`,
      subtitle: `${stats.users.with_2fa} users with 2FA`,
      icon: ShieldCheck,
      testId: 'stat-2fa'
    }
  ];

  const maxLogins = Math.max(...stats.activity_7d.map(d => d.logins), 1);

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => (
          <Card key={card.title} data-testid={card.testId}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{card.title}</p>
                  <p className="text-3xl font-bold mt-1">{card.value}</p>
                  <p className="text-xs text-muted-foreground mt-1">{card.subtitle}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-pista/10 flex items-center justify-center">
                  <card.icon className="h-5 w-5 text-pista" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card data-testid="activity-chart">
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="h-4 w-4 mr-2 text-pista" />
              Logins - Last 7 Days
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.activity_7d.map((day) => (
                <div key={day.date} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground font-mono">{day.date}</span>
                    <span className="font-medium">{day.logins}</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-pista transition-all duration-500"
                      style={{ width: `${(day.logins / maxLogins) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="security-stats">
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertTriangle className="h-4 w-4 mr-2 text-destructive" />
              Security Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-secondary/40 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Successful Logins (24h)</p>
                  <p className="text-xs text-muted-foreground">Legitimate authentications</p>
                </div>
                <p className="text-2xl font-bold text-pista">{stats.activity_24h.successful_logins}</p>
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary/40 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Failed Logins (24h)</p>
                  <p className="text-xs text-muted-foreground">Rejected attempts</p>
                </div>
                <p className="text-2xl font-bold text-destructive">{stats.activity_24h.failed_logins}</p>
              </div>
              <div className="flex items-center justify-between p-3 bg-secondary/40 rounded-lg">
                <div>
                  <p className="font-medium text-sm">Platform Admins</p>
                  <p className="text-xs text-muted-foreground">Users with elevated access</p>
                </div>
                <p className="text-2xl font-bold">{stats.users.admins}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminOverview;
